import React, { useContext, useState } from 'react';
import { AppContext } from '../context/AppContext';
import { ChartBarIcon, ExclamationTriangleIcon, ArrowRightOnRectangleIcon, LockClosedIcon, EnvelopeIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

const Login: React.FC = () => {
  const { login } = useContext(AppContext);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email || !password) {
      setError('Por favor, ingrese correo y contraseña.');
      return;
    }

    setIsLoading(true);

    try {
      await login(email, password);
      // Si funciona, AppContext actualizará 'currentUser' vía onAuthStateChange
      // lo que desmontará este componente automáticamente desde App.tsx
    } catch (err: any) {
      setError(err.message === 'Invalid login credentials'
        ? 'Correo o contraseña incorrectos.'
        : err.message || 'Error al autenticar');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex selection:bg-brand-accent selection:text-white bg-background-light">
      {/* Lado Izquierdo - Imagen Industrial (Oculto en móviles) */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-[#27272A] overflow-hidden items-center justify-center">
        {/* Usamos una imagen de stock de alta calidad de industria/tecnología */}
        <div
          className="absolute inset-0 bg-cover bg-center opacity-40 mix-blend-luminosity transform hover:scale-105 transition-transform duration-1000"
          style={{ backgroundImage: 'url("https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80")' }}
        ></div>

        {/* Capa de gradiente para fusionar elegantemente */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#27272A]/90 to-[#27272A]/40"></div>

        <div className="relative z-10 p-16 text-white max-w-2xl">
          <div className="bg-white/10 p-4 rounded-3xl inline-block backdrop-blur-md border border-white/20 shadow-2xl mb-8">
            <ChartBarIcon className="h-16 w-16 text-brand-accent drop-shadow-lg" />
          </div>
          <h1 className="text-5xl font-black mb-6 tracking-tight leading-tight">Control Inteligente<br />de Consumos</h1>
          <p className="text-xl text-white/80 mb-8 font-medium">Monitorea arranques, registra paradas y gestiona la métrica térmica y eléctrica de la planta en tiempo real.</p>

          <div className="flex items-center space-x-4">
            <div className="flex -space-x-3">
              <div className="h-10 w-10 rounded-full bg-brand-accent border-2 border-[#27272A] flex justify-center items-center text-xs font-bold text-gray-900 shadow-md">RL</div>
              <div className="h-10 w-10 rounded-full bg-purple-500 border-2 border-[#27272A] flex justify-center items-center text-xs font-bold shadow-md">MS</div>
              <div className="h-10 w-10 rounded-full bg-green-500 border-2 border-[#27272A] flex justify-center items-center text-xs font-bold shadow-md">+5</div>
            </div>
            <span className="text-sm font-semibold text-white/60">Operadores activos hoy</span>
          </div>
        </div>
      </div>

      {/* Lado Derecho - Formulario de Login */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 sm:p-12 lg:p-24 bg-white relative">
        <div className="w-full max-w-md animate-in fade-in slide-in-from-bottom-8 duration-700">

          <div className="lg:hidden flex flex-col items-center mb-10">
            <div className="bg-[#27272A] p-4 rounded-2xl shadow-lg mb-4 transform -rotate-3 transition-transform duration-300">
              <ChartBarIcon className="h-10 w-10 text-brand-accent" />
            </div>
            <h1 className="text-3xl font-black text-gray-900 tracking-tight text-center">T-Minus <span className="text-brand-accent">App</span></h1>
            <p className="text-gray-500 font-medium text-center mt-2">Monitor de Consumo Industrial</p>
          </div>

          <div className="hidden lg:block mb-10">
            <h2 className="text-4xl font-black text-gray-900 tracking-tight mb-2">Bienvenido de nuevo</h2>
            <p className="text-lg text-gray-500">Ingresa tus credenciales para acceder al panel.</p>
          </div>

          {error && (
            <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded-r-xl mb-8 flex items-center animate-in fade-in zoom-in-95">
              <ExclamationTriangleIcon className="h-6 w-6 mr-3 flex-shrink-0" />
              <span className="text-sm font-semibold">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Correo Electrónico</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-gray-50 border border-gray-200 text-gray-900 rounded-xl pl-12 pr-4 py-4 focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-all outline-none font-medium"
                  placeholder="ejemplo@gmail.com"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Contraseña</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <LockClosedIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-gray-50 border border-gray-200 text-gray-900 rounded-xl pl-12 pr-12 py-4 focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-all outline-none font-medium"
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-brand-primary transition-colors focus:outline-none"
                  title={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-brand-accent text-gray-900 font-black rounded-xl px-4 py-4 flex items-center justify-center hover:bg-brand-accent-hover hover:shadow-xl shadow-md transform active:scale-[0.98] transition-all focus:outline-none focus:ring-4 focus:ring-brand-accent/30 disabled:opacity-70 disabled:cursor-not-allowed group mt-4 text-lg"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <div className="h-6 w-6 border-3 border-gray-900/30 border-t-gray-900 rounded-full animate-spin mr-3"></div>
                  Validando Accesos...
                </div>
              ) : (
                <>
                  <span className="mr-2">Ingresar al Sistema</span>
                  <ArrowRightOnRectangleIcon className="h-6 w-6 group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </button>
          </form>

          <div className="mt-10 flex items-center justify-center">
            <p className="text-xs text-center text-gray-400 font-medium">
              Protegido mediante Autenticación Segura <br className="lg:hidden" />
              <span className="font-bold text-brand-primary">CodeHive</span>
            </p>
          </div>

        </div>
      </div>
    </div>
  );
};

export default Login;
